/**
 * Matter- and event-level privacy helpers.
 *
 * The rule (from UK barristers' professional-conduct expectations):
 *
 *   A clerk may see ONLY the matters where:
 *     1. They are explicitly on the matter's clerkTeam, OR
 *     2. The barrister has granted them 'Matters' scope access
 *
 *   For any other event on a barrister's diary the clerk sees AVAILABILITY
 *   (time + kind colour) but NOT content — no matter name, no attendees,
 *   no notes, no fee detail.
 *
 *   Admins (chambers head / senior management) bypass this check — they
 *   have chambers-wide oversight.
 *
 *   The barrister themselves always sees their own data.
 */
import type { Matter } from './mattersStore';
import type { CalEvent } from './diaryStore';
import type { ManagedBarrister } from './useClerkWorkspace';

export interface PrincipalLite {
  /** Full display name (e.g. "Sarah Williams") */
  name: string;
  /** 'admin' — always sees content. 'clerk' — subject to clerkTeam. 'barrister' — sees own stuff. */
  role: 'admin' | 'clerk' | 'barrister' | string;
  /** memberType from the auth user, used to distinguish senior_clerk vs junior_clerk */
  memberType?: string;
  /** User UUID — used to match against matter.clerkUserId directly */
  userId?: string;
}

const ADMIN_TYPES = new Set(['admin', 'chambers_admin']);

export function isAdmin(p: PrincipalLite | null | undefined): boolean {
  if (!p) return false;
  return ADMIN_TYPES.has(p.role) || ADMIN_TYPES.has(p.memberType ?? '');
}

/**
 * Can this principal see full content of the given matter?
 *
 *  - admin: always yes
 *  - barrister: yes if they're the matter's barrister
 *  - clerk: yes if:
 *      1. Explicitly in clerkTeam, OR
 *      2. Has 'Matters' scope for the matter's barrister
 *    AND not private.
 *  - private matters: visible to barrister + admin only.
 *  - restricted matters: must be in allowedNames.
 */
export function canSeeMatter(
  principal: PrincipalLite | null | undefined,
  matter: Matter | undefined,
  /** Barristers this clerk manages with their scopes (pass from useClerkWorkspace().managedBarristers) */
  managedBarristers?: ManagedBarrister[],
): boolean {
  if (!principal || !matter) return false;
  if (isAdmin(principal)) return true;

  // Barrister viewing their own matter
  if (
    principal.role === 'barrister' &&
    (matter.barrister === principal.name ||
      principal.name.endsWith(matter.barrister ?? '') ||
      (matter.barrister ?? '').endsWith(principal.name))
  ) {
    return true;
  }

  // Private: barrister + admin only
  if (matter.access === 'private') return false;

  // Restricted: only allowedNames
  if (matter.access === 'restricted') {
    return (matter.allowedNames ?? []).includes(principal.name);
  }

  // Normal + clerk
  if (principal.role === 'clerk') {
    // 1. Check if this clerk is directly assigned on the matter (clerkId match)
    if (principal.userId && matter.clerkUserId && principal.userId === matter.clerkUserId) {
      return true;
    }

    // 2. Check if clerk is explicitly on the clerkTeam
    if (matter.clerkTeam && matter.clerkTeam.length > 0) {
      if (matter.clerkTeam.includes(principal.name)) {
        return true;
      }
    }

    // 3. Check if clerk has 'Matters' scope for the matter's barrister.
    // Prefer UUID match (leadBarristerUserId) over fragile name-string comparison.
    if (managedBarristers) {
      const managed = managedBarristers.find((mb) =>
        (mb.userId && matter.leadBarristerUserId && mb.userId === matter.leadBarristerUserId) ||
        (matter.barrister && (
          mb.name === matter.barrister ||
          matter.barrister.includes(mb.name) ||
          mb.name.includes(matter.barrister)
        ))
      );
      if (managed && managed.scopes.includes('Matters')) {
        return true;
      }
    }

    // If neither condition is met, clerk cannot see this matter
    return false;
  }

  return false;
}

/**
 * Can this principal see full CONTENT of a diary event?
 * (As opposed to just the availability block — time + colour kind.)
 *
 *  - admin: always yes
 *  - barrister: yes if their own event
 *  - clerk: if event is tied to a matter, gate on canSeeMatter. If event
 *           has no matter link (personal/availability block), show content
 *           only if the clerk has 'Diary' scope for this barrister.
 */
export function canSeeEventContent(
  principal: PrincipalLite | null | undefined,
  event: CalEvent | undefined,
  matters: Matter[],
  /** Barristers this clerk manages with their scopes */
  managedBarristers?: ManagedBarrister[],
): boolean {
  if (!principal || !event) return false;
  if (isAdmin(principal)) return true;

  // Barrister viewing their own event
  if (principal.role === 'barrister' && event.barrister) {
    const e = event.barrister;
    if (e === principal.name || principal.name.endsWith(e) || e.endsWith(principal.name)) return true;
  }

  if (principal.role !== 'clerk') return false;

  // Event linked to a matter → show details ONLY if clerk can see the matter
  if (event.caseId) {
    const matter = matters.find((m) => m.id === event.caseId);
    return canSeeMatter(principal, matter, managedBarristers);
  }

  // No matter link → check if clerk has 'Diary' scope for this barrister
  if (managedBarristers && event.barrister) {
    const managed = managedBarristers.find((mb) =>
      mb.name === event.barrister ||
      event.barrister?.includes(mb.name) ||
      mb.name.includes(event.barrister ?? '')
    );
    if (managed && managed.scopes.includes('Diary')) {
      return true;
    }
  }

  // Clerk cannot see this event's details
  return false;
}

/**
 * Returns a redacted placeholder title for an event the viewer may NOT see.
 * Still leaks the KIND (court / conference / task) so scheduling works.
 */
export function redactedEventLabel(event: CalEvent): string {
  switch (event.kind) {
    case 'hearing':
      return 'In court';
    case 'conference':
      return 'Conference';
    case 'task':
      return 'Prep block';
    case 'brief_return':
      return 'Brief return';
    case 'personal':
      return 'Private';
    default:
      return 'Busy';
  }
}
